import {
	Component,
	EventEmitter,
	Input,
	Optional,
	Output,
	Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
	AssociatedContactType,
	BaseClient,
	ClientCreationData,
	ClientType,
	PersonClient,
} from '@models/clients';
import { MatterFileType, MatterClientType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	CreateClientMiniModalComponent,
	CreateClientMiniModalData,
} from 'src/app/private/sale/fields/client/create/modal/modal.component';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { PrefilledNewClient } from '@core/util/llc';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';
import { clientLegalName } from '@core/util/client';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Component({
	selector: 'app-llc-borrower-assign',
	templateUrl: './borrower.component.html',
	standalone: false,
})
export class LLCBorrowerAssignComponent implements ControlValueAccessor {
	public _value: string;
	@Input() label: string;
	@Input() prefilledNewClient: PrefilledNewClient;
	@Input() filterIds: string[] = [];

	@Input() guarantor: boolean = false;
	@Input() signingOfficer: boolean = false;

	@Input() disabled: boolean = false;

	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Input() parentId: string = '';

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	@Input() filterByParent: boolean = false;

	@Input() placeholder: string;
	@Input() title;
	@Input() noResultsMessage: string;

	@Output() onRemove = new EventEmitter<string>();
	constructor(
		@Optional() @Self() public ngControl: NgControl,
		private matDialog: MatDialog,
		private matterClientDispatch: MatterClientDispatch,
		private clientCreationService: ClientCreationService,
	) {
		if (this.ngControl !== null) {
			// Setting the value accessor directly (instead of using the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}
	}

	get value() {
		return this._value;
	}

	set value(val: string) {
		this._value = val;
		this.onChange(val);
		this.onTouched();
	}

	writeValue(value: string): void {
		if (value !== undefined) {
			this.value = value;
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onChange = (_: unknown) => {};

	onTouched: VoidFunction = () => {};

	registerOnChange(fn: (value: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: VoidFunction) {
		this.onTouched = fn;
	}

	getClientTypes(): MatterClientType[] {
		const matterClientTypes: MatterClientType[] = [];
		if (this.guarantor) {
			matterClientTypes.push(MatterClientType.GUARANTOR);
		}

		if (!this.signingOfficer && !this.guarantor) {
			matterClientTypes.push(
				this.fileType === MatterFileType.PURCHASE
					? MatterClientType.BUYER
					: MatterClientType.BORROWER,
			);
		}

		return matterClientTypes;
	}

	create(searchValue) {
		const clientTypes: ClientType[] = [];
		if (this.prefilledNewClient.type === ClientType.PERSON) {
			clientTypes.push(ClientType.PERSON);
		} else {
			clientTypes.push(ClientType.CORPORATION, ClientType.BUILDER);
		}
		const data: CreateClientMiniModalData = {
			client: {
				name: this.prefilledNewClient?.name ?? searchValue,
				firstName: this.prefilledNewClient?.firstName,
				middleName: this.prefilledNewClient?.middleNames,
				lastName: this.prefilledNewClient?.lastName,
				phone: this.prefilledNewClient?.phone,
				workPhone: this.prefilledNewClient?.workPhone,
			},
			clientTypes: clientTypes,
			matterClientRelationship: {
				clientTypes: this.getClientTypes(),
			},
			enableSigningOfficers: false,
			callbackFn: async (ev: ClientCreationData) => {
				if (!ev) return;
				this.value = ev.client.id;
				return this.parentId
					? this.clientCreationService
							.AddNewClientToAssociation(
								{
									associatedContactType: AssociatedContactType.SIGNING_OFFICER,
									parentClientId: this.parentId,
									position: '',
								},
								ev.client as PersonClient,
							)
							.toPromise()
					: new Promise((resolve) => {
							if (this.getClientTypes().includes(MatterClientType.BUYER)) {
								this.matterClientDispatch.CreateAndAddClientToMatterWithOfferor(
									{
										client: ev.client as PersonClient,
										matterClientRelationship: {
											...ev.matterClientRelationship,
											offeror: clientLegalName(
												ev.client,
												this.nameFormattingSettings,
											),
										},
									},
								);
							} else {
								this.matterClientDispatch.CreateAndAddClientToMatter(ev);
							}
							setTimeout(() => {
								resolve();
							}, 1500);
						});
			},
		};
		this.matDialog.open(CreateClientMiniModalComponent, { data });
	}

	// this needs to be looked into - a side effect of removing associations is a refresh of the page causing other non saved selections to be lost
	remove() {
		if (this.parentId) {
			this.clientCreationService
				.RemoveAssociation({
					associatedContactType: AssociatedContactType.SIGNING_OFFICER,
					parentClientId: this.value,
					childClientId: this.parentId, // this is correct I know it is confusing
				})
				.toPromise();
		}

		this.onRemove.emit(this.value);

		this.value = null;
	}

	async select(client: BaseClient) {
		this.value = client.id;

		const offerorName = clientLegalName(
			client as PersonClient,
			this.nameFormattingSettings,
		);

		if (this.isLeapMatter) {
			if (this.parentId) {
				//Create it as an association
				await this.clientCreationService
					.AddNewClientToAssociation(
						{
							associatedContactType: AssociatedContactType.SIGNING_OFFICER,
							parentClientId: this.parentId,
							position: '',
						},
						client as PersonClient,
					)
					.toPromise();
			} else {
				if (this.getClientTypes().includes(MatterClientType.BUYER)) {
					this.matterClientDispatch.CreateAndAddClientToMatterWithOfferor({
						client: client as PersonClient,
						matterClientRelationship: {
							clientTypes: this.getClientTypes(),
							offeror: offerorName,
						},
					});
				} else {
					this.matterClientDispatch.CreateAndAddClientToMatter({
						client: client as PersonClient,
						matterClientRelationship: { clientTypes: this.getClientTypes() },
					});
				}
			}
		} else {
			if (this.parentId) {
				await this.clientCreationService
					.AddExistingClientToAssociation({
						associatedContactType: AssociatedContactType.SIGNING_OFFICER,
						childClientId: client.id,
						parentClientId: this.parentId,
						position: '',
					})
					.toPromise();
			} else {
				this.matterClientDispatch.AddClient({
					clientTypes: this.getClientTypes(),
					id: client.id,
					primaryContact: false,
				});
			}
		}
	}
}
